/**
 * i18n configuration for Manifest.
 *
 * Supported locales:
 *   en  – English (default / fallback)
 *   zh  – Chinese Simplified (placeholder; full translation deferred)
 *
 * To add a new language:
 *   1. Create src/i18n/locales/<lang>/ with all namespace JSON files.
 *   2. Import each namespace below and add to `resources`.
 *   3. Add the locale code to SUPPORTED_LOCALES.
 *
 * Language preference persists in localStorage under the key
 * `manifest-language` (written by i18next-browser-languagedetector).
 */

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

// ── English ──────────────────────────────────────────────────────────────────
import enCommon from "./locales/en/common.json";
import enSettings from "./locales/en/settings.json";
import enCategories from "./locales/en/categories.json";
import enAuth from "./locales/en/auth.json";
import enErrors from "./locales/en/errors.json";

// ── Chinese (placeholder) ─────────────────────────────────────────────────────
import zhCommon from "./locales/zh/common.json";
import zhCategories from "./locales/zh/categories.json";

// ── Constants ─────────────────────────────────────────────────────────────────
export const DEFAULT_LOCALE = "en" as const;
export const SUPPORTED_LOCALES = ["en", "zh"] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

export const LOCALE_LABELS: Record<SupportedLocale, string> = {
  en: "English",
  zh: "中文",
};

// ── Namespaces ─────────────────────────────────────────────────────────────────
export const I18N_NAMESPACES = [
  "common",
  "settings",
  "categories",
  "auth",
  "errors",
] as const;

export type I18nNamespace = (typeof I18N_NAMESPACES)[number];

// ── Initialise ────────────────────────────────────────────────────────────────
/**
 * Resolve a potentially regional language code (e.g. "zh-CN", "en-US")
 * to the base supported locale ("zh", "en"). Returns DEFAULT_LOCALE
 * if the language isn't supported.
 */
export function getSupportedLocale(lang: string | undefined): SupportedLocale {
  if (!lang) return DEFAULT_LOCALE;
  const base = lang.split("-")[0].toLowerCase();
  if ((SUPPORTED_LOCALES as readonly string[]).includes(base)) {
    return base as SupportedLocale;
  }
  return DEFAULT_LOCALE;
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: {
        common: enCommon,
        settings: enSettings,
        categories: enCategories,
        auth: enAuth,
        errors: enErrors,
      },
      zh: {
        common: zhCommon,
        // settings/auth/errors fall back to English until fully translated
        categories: zhCategories,
      },
    },
    lng: undefined, // let LanguageDetector pick from localStorage / browser
    supportedLngs: ["en", "zh"],
    nonExplicitSupportedLngs: true, // zh-CN → zh, en-US → en
    fallbackLng: DEFAULT_LOCALE,
    defaultNS: "common",
    ns: I18N_NAMESPACES,
    detection: {
      // Persist preference in localStorage; read browser lang as fallback
      order: ["localStorage", "navigator"],
      lookupLocalStorage: "manifest-language",
      caches: ["localStorage"],
    },
    interpolation: {
      // React already handles XSS; no need to escape
      escapeValue: false,
    },
    // Log missing keys in development only
    saveMissing: process.env.NODE_ENV === "development",
    missingKeyHandler:
      process.env.NODE_ENV === "development"
        ? (_lngs: readonly string[], ns: string, key: string) =>
            console.warn(`[i18n] Missing key: ${ns}:${key}`)
        : false,
  });

export default i18n;
